import { useAccessToken } from "@/hooks";
import { authorizedGet } from "@/utils/request";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import type { QuestCompletion, QuestCompletions } from "./types";

export const useQuestCompletions = (
  gameId?: string,
  disableRevalidation = false,
  refreshInterval = 0,
) => {
  const getAccessToken = useAccessToken();
  const useSWRFn = disableRevalidation ? useSWRImmutable : useSWR;

  const {
    data: completedQuests,
    isLoading,
    mutate,
    error,
  } = useSWRFn<QuestCompletions>(
    gameId !== undefined ? `questCompletions${gameId}` : "no-game-id",
    async () => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("No access token");
      }
      const response = await authorizedGet<{
        result: QuestCompletion[];
      }>(
        `/v1/quests/quest-completions${gameId ? `?game_id=${gameId}` : ""}`,
        accessToken,
      );
      if (response.result) {
        return Object.fromEntries(
          response.result.map((completion) => [
            completion.external_id,
            completion,
          ]),
        );
      }
      return {};
    },
    {
      ...(disableRevalidation
        ? {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
          }
        : {
            revalidateOnFocus: true,
          }),
      refreshInterval,
    },
  );

  return {
    completedQuests,
    isLoading,
    mutate,
    error,
  };
};

/**
 * This maps using game_id-external_id as the key.
 * This is necessary if fetching completions for ALL quests
 * since external_id is not unique across games.
 */
export const useQuestCompletionsV2 = () => {
  const getAccessToken = useAccessToken();

  const {
    data: completedQuests,
    isLoading,
    mutate,
  } = useSWR<QuestCompletions>(
    "no-game-id",
    async () => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("No access token");
      }
      const response = await authorizedGet<{
        result: QuestCompletion[];
      }>("/v1/quests/quest-completions", accessToken);
      if (response.result) {
        return Object.fromEntries(
          response.result.map((completion) => [
            `${completion.game_id}-${completion.external_id}`,
            completion,
          ]),
        );
      }
      return {};
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 0,
    },
  );

  return {
    completedQuests,
    isLoading,
    mutate,
  };
};
